import React, { useContext, useEffect, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';
import Image from 'next/image';
import posthog from 'posthog-js';
import { getProductFromContext } from '../../context/product';
import { useStore } from '../../store';
import { isUserLoggedIn } from '../../utils/analytics';
import { useWindowSize } from '../../hooks/useWindowSize';

const SocialLinkWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  &:not(:last-child) {
    margin-right: 8px;
  }
`;

const SocialLink = styled.a`
  width: 26px;
  height: 26x;
  border-radius: 22px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    width: 42px;
    height: 42px;
  }
`;

type SocialLinksProps = {
  socialLinks: string | undefined;
};

const SocialLinks: React.FC<SocialLinksProps> = ({ socialLinks }: SocialLinksProps) => {
  const product = getProductFromContext();
  const socialLinksObject = JSON.parse(socialLinks || '{}');
  const { state } = useStore();
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(false);

  const size = useWindowSize();
  const theme = useContext(ThemeContext);

  const linksToIconsMap = {
    twitter: '/assets/icons/twitter-black-icon.svg',
    instagram: '/assets/icons/instagram-black-icon.svg',
    facebook: '/assets/icons/facebook-black-icon.svg',
    youtube: '/assets/icons/youtube-black-icon.svg',
    tiktok: '/assets/icons/tiktok-black-icon.svg',
    discord: '/assets/icons/discord-black-icon.svg',
    twitch: '/assets/icons/twitch-black-icon.svg',
    bluesky: '/assets/icons/bluesky-black-icon.svg',
    other: '/assets/icons/other-black-icon.svg',
  };

  useEffect(() => {
    if (!size.width) {
      return;
    }
    // eslint-disable-next-line react/destructuring-assignment
    const isMobileViewport = size.width < theme.breakpoints.desktop;
    setIsMobileViewport(isMobileViewport);
  }, [size]);

  const socialLinksButtons = Object.entries(socialLinksObject).map(([key, value]) => {
    const valueString = value as string;
    if (!valueString) return null;
    return (
      <SocialLinkWrapper>
        <SocialLink
          target="_blank"
          rel="noreferrer"
          key={key}
          href={valueString || ''}
          onClick={() =>
            posthog.capture(`custom_creator_page_social_click`, {
              social_platform: key,
              link_href: value,
              product_title: product.title,
              product_id: product.id,
              user: state.user?.customer?.email,
              is_logged_in: isUserLoggedIn(state.user),
            })
          }
        >
          <Image
            src={linksToIconsMap[key as keyof typeof linksToIconsMap]}
            height={isMobileViewport ? 26 : 42}
            width={isMobileViewport ? 26 : 42}
            alt={`${key} icon`}
          />
        </SocialLink>
      </SocialLinkWrapper>
    );
  });

  return <>{socialLinksButtons.length > 0 && <>{socialLinksButtons}</>}</>;
};

export default SocialLinks;
